import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Feature, FeatureCollection } from 'geojson';
import * as L from 'leaflet';
import {
  EMPTY,
  Observable,
  catchError,
  defer,
  expand,
  map,
  of,
  reduce,
  shareReplay,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { HelperService } from 'src/app/services/helper.service';
import { environment } from 'src/environments/environment';

export const VIEWPORT_WFS_PAGE_SIZE = 2000;
export const VIEWPORT_BBOX_DEBOUNCE_MS = 250;
/** Fraction of the viewport added around the BBOX (0 = exact viewport). */
export const VIEWPORT_BBOX_PAD = 0;
const VIEWPORT_MAX_PAGES = 2000;

export interface ViewportWfsErrorContext {
  layerName: string;
  bbox: string;
  startIndex: number;
  count: number;
  zoom?: number;
  status?: number;
  statusText?: string;
  geoServerMessage?: string;
}

export interface ViewportWfsError {
  isViewportWfsError: true;
  message: string;
  context: ViewportWfsErrorContext;
}

@Injectable({ providedIn: 'root' })
export class ViewportWfsService {
  private readonly geometryPropertyCache = new Map<string, Observable<string | null>>();

  constructor(
    private readonly http: HttpClient,
    private readonly helperService: HelperService
  ) {}

  private get wfsBase(): string {
    return environment.geoServerUrl.replace(/\/wms\b/i, '/wfs');
  }

  /** `west,south,east,north` in EPSG:4326, padded by VIEWPORT_BBOX_PAD. */
  buildBboxString(bounds: L.LatLngBounds, pad: number = VIEWPORT_BBOX_PAD): string {
    const box = pad > 0 ? bounds.pad(pad) : bounds;
    return [box.getWest(), box.getSouth(), box.getEast(), box.getNorth()]
      .map((value) => this.roundCoordinate(value))
      .join(',');
  }

  buildBboxCql(geometryProperty: string, bounds: L.LatLngBounds, pad?: number): string {
    return `BBOX(${geometryProperty},${this.buildBboxString(bounds, pad)},'EPSG:4326')`;
  }

  /**
   * GeoServer rejects `bbox` and `CQL_FILTER` together, so the viewport box is folded
   * into CQL whenever the caller already has a filter.
   */
  getGeometryProperty(layerName: string): Observable<string | null> {
    const cached = this.geometryPropertyCache.get(layerName);
    if (cached) return cached;

    const params = new URLSearchParams({
      service: 'WFS',
      version: '2.0.0',
      request: 'DescribeFeatureType',
      typeName: layerName,
      outputFormat: 'application/json',
    });

    const request$ = this.http.get<any>(`${this.wfsBase}?${params.toString()}`).pipe(
      map((schema) => this.pickGeometryProperty(schema)),
      catchError(() => of(null)),
      tap((property) => {
        if (!property) this.geometryPropertyCache.delete(layerName);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    this.geometryPropertyCache.set(layerName, request$);
    return request$;
  }

  /**
   * Sequentially pages every feature intersecting `bounds`, so GeoServer never has to
   * encode the whole layer in one response. Completes once the last page is merged.
   */
  loadFeaturesForBounds(
    layerName: string,
    baseCql: string | null | undefined,
    bounds: L.LatLngBounds,
    options?: { zoom?: number; pageSize?: number }
  ): Observable<FeatureCollection> {
    return this.getGeometryProperty(layerName).pipe(
      switchMap((geometryProperty) => this.loadPages(layerName, baseCql, bounds, geometryProperty, options))
    );
  }

  /** Full-scene calculations need every matching feature, independent of map bounds. */
  loadAllFeatures(
    layerName: string,
    baseCql?: string | null,
    options?: { pageSize?: number }
  ): Observable<FeatureCollection> {
    return this.loadPages(layerName, baseCql, null, null, options);
  }

  /** Check the full filtered layer without downloading or paging through its features. */
  hasAnyMatchingFeature(layerName: string, baseCql?: string | null): Observable<boolean> {
    const params = this.helperService.makeWfsParams(layerName, { count: 1 });
    if (baseCql) params.set('CQL_FILTER', baseCql);

    return this.http.get<FeatureCollection>(`${this.wfsBase}?${params.toString()}`).pipe(
      map((collection) => {
        if (!Array.isArray(collection?.features)) {
          throw new Error('Invalid WFS feature collection.');
        }
        return collection.features.length > 0;
      }),
      catchError((error) => throwError(() => error?.isViewportWfsError ? error : this.buildError(
        error,
        { layerName, bbox: 'full-scene', startIndex: 0, count: 1 }
      )))
    );
  }

  private loadPages(
    layerName: string,
    baseCql: string | null | undefined,
    bounds: L.LatLngBounds | null,
    geometryProperty: string | null,
    options?: { zoom?: number; pageSize?: number }
  ): Observable<FeatureCollection> {
    const pageSize = options?.pageSize ?? VIEWPORT_WFS_PAGE_SIZE;
    return defer(() => {
      let loaded = 0;
      const fetchPage = (startIndex: number) => this.fetchPage(
        layerName, baseCql, bounds, geometryProperty, startIndex, pageSize, options?.zoom
      );

      return fetchPage(0).pipe(
        expand((page, index) => {
          const returned = page.features.length;
          loaded += returned;
          const rawMatched = (page as any)?.numberMatched;
          const matched = rawMatched == null || Number(rawMatched) < 0 ? NaN : Number(rawMatched);
          if (Number.isFinite(matched) && loaded >= matched) return EMPTY;
          if (returned === 0 && Number.isFinite(matched) && loaded < matched) {
            return throwError(() => new Error('WFS returned an incomplete feature collection.'));
          }
          if (returned === 0 || (!Number.isFinite(matched) && returned < pageSize)) return EMPTY;
          if (index + 1 >= VIEWPORT_MAX_PAGES) {
            return throwError(() => new Error('WFS pagination limit reached before all features were loaded.'));
          }
          return fetchPage(loaded);
        }),
        reduce(
          (accumulator, page) => this.mergeFeatures(accumulator, page),
          new Map<string, Feature>()
        ),
        map((featureMap) => ({
          type: 'FeatureCollection' as const,
          features: Array.from(featureMap.values()),
        })),
        catchError((error) => throwError(() => error?.isViewportWfsError ? error : this.buildError(
          new HttpErrorResponse({ error: error?.message }),
          { layerName, bbox: bounds ? this.buildBboxString(bounds) : 'full-scene', startIndex: loaded, count: pageSize, zoom: options?.zoom }
        )))
      );
    });
  }

  private fetchPage(
    layerName: string,
    baseCql: string | null | undefined,
    bounds: L.LatLngBounds | null,
    geometryProperty: string | null,
    startIndex: number,
    count: number,
    zoom?: number
  ): Observable<FeatureCollection> {
    const params = this.helperService.makeWfsParams(layerName, { count, startIndex });
    const bbox = bounds ? this.buildBboxString(bounds) : 'full-scene';

    if (!bounds) {
      if (baseCql) params.set('CQL_FILTER', baseCql);
    } else if (geometryProperty) {
      const cql = this.helperService.combineFilters(
        baseCql ?? '',
        this.buildBboxCql(geometryProperty, bounds)
      );
      params.set('CQL_FILTER', cql);
    } else {
      // No geometry attribute available: the native bbox param only works without CQL_FILTER.
      if (baseCql) {
        return throwError(() => this.buildError(
          new HttpErrorResponse({ error: 'Unable to identify the geometry property for viewport filtering.' }),
          { layerName, bbox, startIndex, count, zoom }
        ));
      }
      params.set('bbox', `${bbox},EPSG:4326`);
    }

    return this.http.get<FeatureCollection>(`${this.wfsBase}?${params.toString()}`).pipe(
      map((page) => {
        if (!Array.isArray(page?.features)) throw new Error('Invalid WFS feature collection.');
        return page;
      }),
      catchError((error: HttpErrorResponse) =>
        throwError(() =>
          this.buildError(error, { layerName, bbox, startIndex, count, zoom })
        )
      )
    );
  }

  private mergeFeatures(
    accumulator: Map<string, Feature>,
    page: FeatureCollection
  ): Map<string, Feature> {
    const features = Array.isArray(page?.features) ? page.features : [];
    features.forEach((feature) => {
      // Size already advances after each insertion. Adding the page index again
      // leaves gaps that can collide with anonymous features on the next page.
      accumulator.set(this.featureKey(feature, accumulator.size), feature);
    });
    return accumulator;
  }

  private featureKey(feature: Feature, fallbackIndex: number): string {
    const id =
      (feature as any)?.id ??
      (feature?.properties as any)?.fid ??
      (feature?.properties as any)?.id;
    return id != null ? String(id) : `__idx_${fallbackIndex}`;
  }

  private pickGeometryProperty(schema: any): string | null {
    const properties = schema?.featureTypes?.[0]?.properties ?? [];
    const geometry = properties.find((property: any) =>
      String(property?.type ?? '').startsWith('gml:')
    );
    return geometry?.name ?? null;
  }

  private buildError(
    error: HttpErrorResponse,
    context: Omit<ViewportWfsErrorContext, 'status' | 'statusText' | 'geoServerMessage'>
  ): ViewportWfsError {
    const fullContext: ViewportWfsErrorContext = {
      ...context,
      status: error?.status,
      statusText: error?.statusText,
      geoServerMessage: this.extractGeoServerMessage(error),
    };

    return {
      isViewportWfsError: true,
      message: `Viewport WFS request failed (${error?.status ?? 'unknown'})`,
      context: fullContext,
    };
  }

  private extractGeoServerMessage(error: HttpErrorResponse): string | undefined {
    const body = error?.error;
    const raw =
      typeof body === 'string' ? body : body?.exceptionText ?? body?.message ?? error?.message;
    return typeof raw === 'string' ? raw.slice(0, 500) : undefined;
  }

  private roundCoordinate(value: number): number {
    return Math.round(value * 1e6) / 1e6;
  }
}
